import type { LocalizedDict } from '../index'
import type { OrgTypeCode } from '../../lib/org-type-labels'

// Role-aware welcome tour content (WelcomePage) — EN + TR.
// Moved here from features/onboarding/tour-content.ts, which re-exports the
// English map as ROLE_TOURS for existing callers. `highlight` names the
// sidebar item being pointed to, so TR uses the Turkish sidebar labels.
// SELLER-LEAK INVARIANT (see welcome.ts): each role only sees its own entry.

export type TourStep = {
  title: string
  body: string
  highlight: string // Sidebar item label being pointed to (visual cue)
}

export type ChecklistItem = {
  label: string
  hint: string
}

export type RoleTour = {
  /** Where to send them after tour finish (role-default dashboard) */
  defaultRoute: string
  /** Short tagline shown under welcome header */
  tagline: string
  /** 3 tour steps */
  steps: [TourStep, TourStep, TourStep]
  /** 3-5 getting-started checklist items */
  checklist: ChecklistItem[]
}

export const roleToursI18n: LocalizedDict<Record<OrgTypeCode, RoleTour>> = {
  en: {
    A1: {
      defaultRoute: '/admin',
      tagline: 'Platform owner — full governance surface (system-seed only; invited tour not used).',
      steps: [
        {
          title: 'Sidebar = your control center',
          body: 'All admin tools live in the left sidebar. Hover any group to expand it.',
          highlight: 'Orders',
        },
        {
          title: 'Activity feed',
          body: 'Every operational event lands here. Use it to trace cross-actor flows.',
          highlight: 'Activity',
        },
        {
          title: 'Need help?',
          body: 'Settings is at the bottom of the sidebar. The Demo Guide explains every surface.',
          highlight: 'Settings',
        },
      ],
      checklist: [
        { label: 'Review platform settings', hint: 'Settings → Platform · ensure defaults match your operation' },
        { label: 'Check audit retention', hint: 'Stage 5 backend: 3-month audit partition policy' },
      ],
    },
    A2: {
      defaultRoute: '/admin',
      tagline: 'PEXIFY Internal Staff — full admin surface (tier-based caps in V1.5+).',
      steps: [
        {
          title: 'Your daily queues',
          body: 'Pending Updates, Vendor Replacements, Catalog Issues, Unmapped SKUs — all in the Queues group.',
          highlight: 'Queues',
        },
        {
          title: 'Catalog stewardship',
          body: 'Master catalog products + categories live under Catalog. You can edit, archive, or activate drafts.',
          highlight: 'Catalog',
        },
        {
          title: 'Onboarding panel',
          body: 'Send invitations to partners (SO2/SO3) and external sellers (BO2) from the Onboarding entry.',
          highlight: 'Onboarding',
        },
      ],
      checklist: [
        { label: 'Open the Pending Updates queue', hint: 'See vendor-submitted catalog changes awaiting review' },
        { label: 'Browse the master catalog', hint: 'Catalog → Products · understand the SKU space' },
        { label: 'Open your team task list', hint: 'Workspace → Tasks for daily ops planning' },
      ],
    },
    BO1: {
      defaultRoute: '/seller',
      tagline: 'Internal seller (PEXIFY-owned store) — V1.5+ delegation pattern.',
      steps: [
        {
          title: 'Your store orders',
          body: 'Incoming Etsy/Shopify orders land in My Orders. Click any order to see fulfillment status.',
          highlight: 'My Orders',
        },
        {
          title: 'Product catalog',
          body: 'Manage your listed products under My Products.',
          highlight: 'My Products',
        },
        {
          title: 'Reports',
          body: 'Submit catalog change requests via My Reports.',
          highlight: 'My Reports',
        },
      ],
      checklist: [
        { label: 'Connect your store integration', hint: 'Etsy/Shopify OAuth flow — Stage 5 + Priority 10' },
        { label: 'Verify your inventory levels', hint: 'Inventory tab in your dashboard' },
      ],
    },
    BO2: {
      defaultRoute: '/seller',
      tagline: 'External seller — connect your Etsy/Shopify and source from PEXIFY catalog.',
      steps: [
        {
          title: 'Connect your stores',
          body: "First step: connect your Etsy/Shopify store via OAuth. PEXIFY pulls your orders automatically.",
          highlight: 'My Products',
        },
        {
          title: 'Source from master catalog',
          body: 'Browse PEXIFY-curated products + add to your store assortment.',
          highlight: 'My Products',
        },
        {
          title: 'Track fulfillment',
          body: "PEXIFY handles production + shipping. You track each order's status here.",
          highlight: 'My Orders',
        },
      ],
      checklist: [
        { label: 'Connect your first Etsy/Shopify store', hint: 'OAuth flow — Stage 5 + Priority 10 (Multi-store coord)' },
        { label: 'Browse the master catalog', hint: 'See what products are available for sourcing' },
        { label: 'Set up payment receivables', hint: 'Wallet → Bank account (Stage 5 ratify)' },
        { label: 'Place your first test order', hint: 'Once a real customer orders on your Etsy store, it flows here automatically' },
      ],
    },
    BO3: {
      defaultRoute: '/admin', // V1: admin-mediated push (Q-018); b2b-portal V2+
      tagline: 'B2B wholesale buyer — admin-mediated push catalog in V1.',
      steps: [
        {
          title: 'Wholesale catalog',
          body: 'Your account manager will share product packages tailored to your business.',
          highlight: 'B2B Push',
        },
        {
          title: 'Place orders',
          body: 'Order from shared packages. Bulk pricing applies automatically.',
          highlight: 'My Orders',
        },
        {
          title: 'Account contact',
          body: "Reach out to your PEXIFY account manager for catalog requests or quotes.",
          highlight: 'Settings',
        },
      ],
      checklist: [
        { label: 'Wait for your first catalog push', hint: 'Admin sends curated product packages your way (V1 model)' },
        { label: 'Confirm your shipping address', hint: 'Settings → Account · for wholesale shipments' },
      ],
    },
    SO1: {
      defaultRoute: '/supply',
      tagline: 'In-house PEXIFY production atelier.',
      steps: [
        {
          title: 'Work orders queue',
          body: 'Production work orders assigned to your atelier land in My Work Orders.',
          highlight: 'My Work Orders',
        },
        {
          title: 'State transitions',
          body: "Mark each WO 'started → completed → shipped' as production progresses.",
          highlight: 'My Work Orders',
        },
        {
          title: 'Submit catalog updates',
          body: 'Request changes to product specs via My Submitted Updates.',
          highlight: 'My Submitted Updates',
        },
      ],
      checklist: [
        { label: 'Set up your production capacity', hint: 'Daily output, lead times — Stage 5 ratify' },
        { label: 'Review your assigned product catalog', hint: 'What SKUs are routed to your atelier' },
      ],
    },
    SO2: {
      defaultRoute: '/supply',
      tagline: 'Production partner (external atelier) — fulfill work orders from PEXIFY.',
      steps: [
        {
          title: 'Incoming work orders',
          body: "Orders assigned to your atelier appear in My Work Orders. New ones at the top.",
          highlight: 'My Work Orders',
        },
        {
          title: 'Production updates',
          body: 'Update each WO state as production progresses. Upload production photos at completion.',
          highlight: 'My Work Orders',
        },
        {
          title: 'Catalog change requests',
          body: 'Need to flag a product spec change? Use My Submitted Updates.',
          highlight: 'My Submitted Updates',
        },
      ],
      checklist: [
        { label: 'Review your assigned product range', hint: 'What SKUs PEXIFY routes to you' },
        { label: 'Set your production capacity defaults', hint: 'Daily output + lead times' },
        { label: 'Wait for your first work order', hint: 'New WOs appear here once admin assigns' },
        { label: 'Connect your payout banking', hint: 'Wallet → Bank account · production payouts' },
      ],
    },
    SO3: {
      defaultRoute: '/supply',
      tagline: 'Vendor / wholesaler — supply finished goods to PEXIFY.',
      steps: [
        {
          title: 'Your catalog',
          body: 'Manage the products you offer to PEXIFY in My Products.',
          highlight: 'My Products',
        },
        {
          title: 'Procurement orders',
          body: 'Orders PEXIFY places with you appear in My Work Orders (vendor-side view).',
          highlight: 'My Work Orders',
        },
        {
          title: 'Submit catalog updates',
          body: 'New product additions or price changes go through My Submitted Updates.',
          highlight: 'My Submitted Updates',
        },
      ],
      checklist: [
        { label: 'Review your current SKU mappings', hint: 'How your products map to PEXIFY catalog' },
        { label: 'Set lead times + MOQs', hint: 'Per-product (Wave 2)' },
        { label: 'Connect your payout banking', hint: 'Wallet → Bank account · vendor payouts' },
      ],
    },
  },
  tr: {
    A1: {
      defaultRoute: '/admin',
      tagline: 'Platform sahibi — tüm yönetim ekranları (yalnızca sistem kurulumu; davet turu kullanılmaz).',
      steps: [
        {
          title: 'Sol menü = kontrol merkeziniz',
          body: 'Tüm yönetim araçları soldaki menüde. Açmak için bir grubun üzerine gelin.',
          highlight: 'Siparişler',
        },
        {
          title: 'Hareket akışı',
          body: 'Her operasyon olayı buraya düşer. Taraflar arası akışları buradan takip edin.',
          highlight: 'Aktivite',
        },
        {
          title: 'Yardım mı lazım?',
          body: 'Ayarlar menünün en altında. Demo Rehberi her ekranı anlatır.',
          highlight: 'Ayarlar',
        },
      ],
      checklist: [
        { label: 'Platform ayarlarını gözden geçirin', hint: 'Ayarlar → Platform · varsayılanların işinize uyduğundan emin olun' },
        { label: 'Denetim kaydı saklama süresini kontrol edin', hint: 'Aşama 5 arka uç: 3 aylık denetim bölümleme kuralı' },
      ],
    },
    A2: {
      defaultRoute: '/admin',
      tagline: 'PEXIFY iç ekip — tüm yönetim ekranları (kademeye göre sınırlar V1.5+).',
      steps: [
        {
          title: 'Günlük kuyruklarınız',
          body: 'Bekleyen güncellemeler, tedarikçi değişimleri, katalog sorunları, eşlenmemiş SKU’lar — hepsi Kuyruklar grubunda.',
          highlight: 'Kuyruklar',
        },
        {
          title: 'Katalog yönetimi',
          body: 'Ana katalog ürünleri ve kategoriler Katalog altında. Taslakları düzenleyebilir, arşivleyebilir ya da yayına alabilirsiniz.',
          highlight: 'Katalog',
        },
        {
          title: 'Davet paneli',
          body: 'Ortaklara (SO2/SO3) ve dış satıcılara (BO2) davetleri Satıcı Başvuruları bölümünden gönderin.',
          highlight: 'Satıcı Başvuruları',
        },
      ],
      checklist: [
        { label: 'Bekleyen güncellemeler kuyruğunu açın', hint: 'Tedarikçilerin gönderdiği, onay bekleyen katalog değişikliklerini görün' },
        { label: 'Ana kataloğa göz atın', hint: 'Katalog → Ürünler · SKU yapısını tanıyın' },
        { label: 'Ekip görev listenizi açın', hint: 'Çalışma alanı → Görevler · günlük iş planı için' },
      ],
    },
    BO1: {
      defaultRoute: '/seller',
      tagline: 'İç satıcı (PEXIFY’a ait mağaza) — V1.5+ devir modeli.',
      steps: [
        {
          title: 'Mağaza siparişleriniz',
          body: 'Gelen Etsy/Shopify siparişleri Siparişlerim’e düşer. Gönderim durumunu görmek için bir siparişe tıklayın.',
          highlight: 'Siparişlerim',
        },
        {
          title: 'Ürün kataloğu',
          body: 'Listelediğiniz ürünleri Ürünlerim altında yönetin.',
          highlight: 'Ürünlerim',
        },
        {
          title: 'Raporlar',
          body: 'Katalog değişiklik taleplerini Raporlarım üzerinden gönderin.',
          highlight: 'Raporlarım',
        },
      ],
      checklist: [
        { label: 'Mağaza entegrasyonunuzu bağlayın', hint: 'Etsy/Shopify OAuth akışı — Aşama 5 + Öncelik 10' },
        { label: 'Stok seviyelerinizi kontrol edin', hint: 'Panelinizdeki Stok sekmesi' },
      ],
    },
    BO2: {
      defaultRoute: '/seller',
      tagline: 'Dış satıcı — Etsy/Shopify mağazanızı bağlayın, ürünleri PEXIFY kataloğundan alın.',
      steps: [
        {
          title: 'Mağazalarınızı bağlayın',
          body: 'İlk adım: Etsy/Shopify mağazanızı OAuth ile bağlayın. PEXIFY siparişlerinizi otomatik çeker.',
          highlight: 'Ürünlerim',
        },
        {
          title: 'Ana katalogdan ürün seçin',
          body: 'PEXIFY’ın seçtiği ürünlere göz atın ve mağaza ürün yelpazenize ekleyin.',
          highlight: 'Ürünlerim',
        },
        {
          title: 'Gönderimi takip edin',
          body: 'Üretim ve kargoyu PEXIFY üstlenir. Her siparişin durumunu buradan takip edersiniz.',
          highlight: 'Siparişlerim',
        },
      ],
      checklist: [
        { label: 'İlk Etsy/Shopify mağazanızı bağlayın', hint: 'OAuth akışı — Aşama 5 + Öncelik 10 (çoklu mağaza)' },
        { label: 'Ana kataloğa göz atın', hint: 'Hangi ürünleri alabileceğinizi görün' },
        { label: 'Ödeme alma bilgilerinizi girin', hint: 'Cüzdan → Banka bilgileri (Aşama 5)' },
        { label: 'İlk deneme siparişinizi verin', hint: 'Etsy mağazanıza gerçek bir sipariş geldiğinde otomatik olarak buraya akar' },
      ],
    },
    BO3: {
      defaultRoute: '/admin', // V1: admin-mediated push (Q-018); b2b-portal V2+
      tagline: 'B2B toptan alıcı — V1’de katalog yönetici aracılığıyla gönderilir.',
      steps: [
        {
          title: 'Toptan katalog',
          body: 'Hesap yöneticiniz işinize uygun ürün paketlerini sizinle paylaşır.',
          highlight: 'B2B Push',
        },
        {
          title: 'Sipariş verin',
          body: 'Paylaşılan paketlerden sipariş verin. Toplu fiyat otomatik uygulanır.',
          highlight: 'Siparişlerim',
        },
        {
          title: 'Hesap iletişimi',
          body: 'Katalog talepleri ya da fiyat teklifi için PEXIFY hesap yöneticinize yazın.',
          highlight: 'Ayarlar',
        },
      ],
      checklist: [
        { label: 'İlk katalog gönderimini bekleyin', hint: 'Yönetici size seçilmiş ürün paketleri gönderir (V1 modeli)' },
        { label: 'Teslimat adresinizi onaylayın', hint: 'Ayarlar → Hesap · toptan gönderimler için' },
      ],
    },
    SO1: {
      defaultRoute: '/supply',
      tagline: 'PEXIFY’ın kendi üretim atölyesi.',
      steps: [
        {
          title: 'İş emri kuyruğu',
          body: 'Atölyenize atanan üretim iş emirleri İş Emirlerim’e düşer.',
          highlight: 'İş Emirlerim',
        },
        {
          title: 'Durum geçişleri',
          body: 'Üretim ilerledikçe her iş emrini ‘başladı → tamamlandı → gönderildi’ olarak işaretleyin.',
          highlight: 'İş Emirlerim',
        },
        {
          title: 'Katalog güncellemesi gönderin',
          body: 'Ürün özelliklerindeki değişiklikleri Gönderilen Güncellemelerim üzerinden talep edin.',
          highlight: 'Gönderilen Güncellemelerim',
        },
      ],
      checklist: [
        { label: 'Üretim kapasitenizi girin', hint: 'Günlük üretim, teslim süreleri — Aşama 5' },
        { label: 'Size atanan ürün kataloğunu inceleyin', hint: 'Atölyenize hangi SKU’ların yönlendirildiği' },
      ],
    },
    SO2: {
      defaultRoute: '/supply',
      tagline: 'Üretim ortağı (dış atölye) — PEXIFY’dan gelen iş emirlerini üretin.',
      steps: [
        {
          title: 'Gelen iş emirleri',
          body: 'Atölyenize atanan siparişler İş Emirlerim’de görünür. Yeniler en üstte.',
          highlight: 'İş Emirlerim',
        },
        {
          title: 'Üretim güncellemeleri',
          body: 'Üretim ilerledikçe her iş emrinin durumunu güncelleyin. Bitince üretim fotoğraflarını yükleyin.',
          highlight: 'İş Emirlerim',
        },
        {
          title: 'Katalog değişiklik talepleri',
          body: 'Bir ürün özelliğinde değişiklik mi bildireceksiniz? Gönderilen Güncellemelerim’i kullanın.',
          highlight: 'Gönderilen Güncellemelerim',
        },
      ],
      checklist: [
        { label: 'Size atanan ürün yelpazesini inceleyin', hint: 'PEXIFY’ın size yönlendirdiği SKU’lar' },
        { label: 'Varsayılan üretim kapasitenizi girin', hint: 'Günlük üretim + teslim süreleri' },
        { label: 'İlk iş emrinizi bekleyin', hint: 'Yönetici atadığında yeni iş emirleri burada görünür' },
        { label: 'Ödeme alacağınız banka bilgilerini bağlayın', hint: 'Cüzdan → Banka bilgileri · üretim ödemeleri' },
      ],
    },
    SO3: {
      defaultRoute: '/supply',
      tagline: 'Tedarikçi / toptancı — PEXIFY’a hazır ürün tedarik edin.',
      steps: [
        {
          title: 'Kataloğunuz',
          body: 'PEXIFY’a sunduğunuz ürünleri Ürünlerim’de yönetin.',
          highlight: 'Ürünlerim',
        },
        {
          title: 'Satın alma siparişleri',
          body: 'PEXIFY’ın size verdiği siparişler İş Emirlerim’de görünür (tedarikçi görünümü).',
          highlight: 'İş Emirlerim',
        },
        {
          title: 'Katalog güncellemesi gönderin',
          body: 'Yeni ürün eklemeleri ya da fiyat değişiklikleri Gönderilen Güncellemelerim üzerinden gider.',
          highlight: 'Gönderilen Güncellemelerim',
        },
      ],
      checklist: [
        { label: 'Mevcut SKU eşleştirmelerinizi inceleyin', hint: 'Ürünlerinizin PEXIFY kataloğuyla nasıl eşleştiği' },
        { label: 'Teslim süreleri + minimum sipariş adetlerini girin', hint: 'Ürün bazında (Dalga 2)' },
        { label: 'Ödeme alacağınız banka bilgilerini bağlayın', hint: 'Cüzdan → Banka bilgileri · tedarikçi ödemeleri' },
      ],
    },
  },
}
